/**
 * Idiomas da interface.
 *
 * Só o padrão entra no chunk de entrada: o visitante anônimo da vitrine não
 * deve baixar traduções de um idioma que nenhum curso dele usa. Os demais
 * chegam quando um curso declara `locale` no manifesto.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ptBR from '../locales/pt-BR.json';

export const DEFAULT_LOCALE = 'pt-BR';

/** Idiomas além do padrão, carregados sob demanda. */
const LOCALE_LOADERS: Record<string, () => Promise<{ default: object }>> = {
  es: () => import('../locales/es.json'),
};

i18n.use(initReactI18next).init({
  resources: {
    'pt-BR': { translation: ptBR },
  },
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

/**
 * Deixa o idioma pronto para uso e o aplica.
 *
 * Precisa ser aguardado antes de montar a tela: com `useSuspense: false`,
 * trocar de idioma antes de as traduções chegarem mostraria as chaves cruas.
 */
export async function ensureLocale(locale: string): Promise<void> {
  if (!i18n.hasResourceBundle(locale, 'translation')) {
    const load = LOCALE_LOADERS[locale];
    if (!load) return; // idioma desconhecido: segue no padrão
    const { default: resources } = await load();
    i18n.addResourceBundle(locale, 'translation', resources);
  }

  if (i18n.language !== locale) {
    await i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
  }
}

export default i18n;
